import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { User } from '../models';

interface CreateUserRequest {
  username: string;
  email: string;
  password: string;
  role: string;
  status: string;
}

interface UpdateUserRequest {
  username: string;
  email: string;
  role: string;
  status: string;
  password?: string;
}

interface UserResponse {
  id: number;
  username: string;
  email: string;
  role: string;
  status: string;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
  pagesCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private apiUrl = '/api/users';

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHeaders(): HttpHeaders {
    return this.authService.getAuthHeaders();
  }

  getAllUsers(): Observable<User[]> {
    return this.http.get<UserResponse[]>(this.apiUrl, {
      headers: this.getHeaders()
    }).pipe(
      map(users => users.map(u => this.mapToUser(u)))
    );
  }

  getUserById(id: number): Observable<User> {
    return this.http.get<UserResponse>(`${this.apiUrl}/${id}`, {
      headers: this.getHeaders()
    }).pipe(
      map(user => this.mapToUser(user))
    );
  }

  createUser(request: CreateUserRequest): Observable<User> {
    return this.http.post<UserResponse>(this.apiUrl, request, {
      headers: this.getHeaders()
    }).pipe(
      map(user => this.mapToUser(user))
    );
  }

  updateUser(id: number, request: UpdateUserRequest): Observable<User> {
    return this.http.put<UserResponse>(`${this.apiUrl}/${id}`, request, {
      headers: this.getHeaders()
    }).pipe(
      map(user => this.mapToUser(user))
    );
  }

  deleteUser(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`, {
      headers: this.getHeaders()
    });
  }

  private mapToUser(response: UserResponse): User {
    return {
      id: response.id,
      name: response.username,
      email: response.email,
      role: response.role,
      status: response.status as 'active' | 'inactive',
      createdAt: new Date(response.createdAt),
      lastLogin: response.lastLogin ? new Date(response.lastLogin) : undefined
    };
  }
}